// frontend/src/pages/DashboardPage.tsx
import { useEffect, useMemo, useState, type CSSProperties } from "react";
import { Link } from "react-router-dom";
import {
  Line,
  BarChart,
  Bar,
  ComposedChart,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import { api } from "../api/client.js";
import { type ChartPoint } from "../api/charts.js";
import { Card } from "../components/Card.js";
import { PageHeader } from "../components/PageHeader.js";
import { DateRangeChips } from "../components/DateRangeChips.js";
import { AudiencePanel } from "../components/AudiencePanel.js";
import { fetchPnL, type PnL } from "../api/sales.js";
import { SectionTitle } from "../components/SectionTitle.js";
import { Clock } from "lucide-react";
import { HourHeatmap } from "../components/HourHeatmap.js";
import {
  fetchHourPatterns, type HourPattern,
} from "../api/analytics.js";
import { fetchDashboardExtended, type DashboardExtended, type SalesBySource, type AttributionModels, type TrafficSources, type AdPurchaseLag, type BuyerJourney, type AdReconciliation, type BotStats } from "../api/dashboard-extended.js";
import { type MetricId } from "../charts/METRICS.js";
import { chartTheme } from "../charts/chart-theme.js";
import { formatRub, formatPct, formatInt } from "../charts/format.js";
import { MetricCard } from "../components/MetricCard.js";
import { MetricSection } from "../components/MetricSection.js";
import { ComparisonSelector, comparisonLabel } from "../components/ComparisonSelector.js";
import { usePeriodAndComparison } from "../hooks/usePeriodAndComparison.js";
import { KPIStrip, type KPIStripItem } from "../components/KPIStrip.js";
import { FunnelHero, type FunnelHeroStep } from "../components/FunnelHero.js";
import { ComboTrendChart } from "../components/ComboTrendChart.js";
import { CRRibbon } from "../components/CRRibbon.js";

// Shape of `/api/metrics/dashboard` (Task 14/16). Backend types live in
// backend/src/core/db/queries/metrics/*.ts — здесь дублируем минимально как `any`,
// потому что значения метрик читаем по строковому ключу `MetricId`.
interface DashboardMetricsSection {
  current: Record<string, number | null>;
  previous: Record<string, number | null>;
  sparklines: Record<string, Array<{ day: string; value: number | null }>>;
}

interface DashboardMetricsResponse {
  money: DashboardMetricsSection;
  reach: DashboardMetricsSection;
  cpa: DashboardMetricsSection;
  cr: DashboardMetricsSection;
  quality: DashboardMetricsSection;
  period: { current: unknown; previous: unknown; vs: string; days: number };
}

// MetricCard ждёт ChartPoint[]. Бэкенд /api/metrics/dashboard отдаёт
// [{day, value}]. Sparkline читает (p as Record<string, ...>)[metricId],
// поэтому мапим value → metricId-ключ и кастуем как ChartPoint[].
function adaptSparkline(
  metricId: MetricId,
  series: Array<{ day: string; value: number | null }> | undefined,
): ChartPoint[] {
  if (!series || series.length === 0) return [];
  return series.map((p) => ({ day: p.day, [metricId]: p.value })) as unknown as ChartPoint[];
}

interface PortfolioSummary {
  period_days: number;
  total_cost_rub: number;
  total_gross_revenue: number;
  total_net_revenue: number;
  net_roas: number | null;
  by_role: Record<
    string,
    {
      campaigns_count: number;
      cost_rub: number;
      net_revenue: number;
      net_roas: number | null;
    }
  >;
}

interface CampaignMetrics {
  campaign_id: number;
  name: string;
  type: string;
  state: string;
  role: string;
  total_cost_rub: number;
  total_purchases: number;
  total_net_revenue: number;
  net_roas: number | null;
}

interface DashboardResponse {
  period_days: number;
  portfolio: PortfolioSummary;
  campaigns: CampaignMetrics[];
}

// computeDaysFromRange — кол-во дней в [from..to] inclusive, для legacy fetch'ей
// которые принимают `days` (summary, profit, extended).
function computeDaysFromRange(from: string, to: string): number {
  const a = Date.parse(from);
  const b = Date.parse(to);
  if (Number.isNaN(a) || Number.isNaN(b) || b < a) return 30;
  return Math.round((b - a) / (24 * 60 * 60 * 1000)) + 1;
}

export function DashboardPage() {
  const { state: periodState, setPreset, setRange, setVs, apiQueryString } = usePeriodAndComparison();
  // legacy days — для summary / profit / extended fetch'ей
  const days = useMemo(
    () => computeDaysFromRange(periodState.from, periodState.to),
    [periodState.from, periodState.to],
  );

  const [allMetrics, setAllMetrics] = useState<DashboardMetricsResponse | null>(null);
  const [data, setData] = useState<DashboardResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState<string | null>(null);
  const [hours, setHours] = useState<HourPattern[] | null>(null);
  const [extended, setExtended] = useState<DashboardExtended | null>(null);
  const [pnl, setPnl] = useState<PnL | null>(null); // КИТ P&L — прибыль/маржинальность от выручки

  // Summary портфеля по `days` (sparkline-серии теперь приходят из /api/metrics/dashboard)
  useEffect(() => {
    setLoading(true);
    setError(null);
    api<DashboardResponse>(`/api/dashboard/summary?days=${days}`)
      .then((summary) => setData(summary))
      .catch((e) => setError((e as Error).message))
      .finally(() => setLoading(false));
  }, [days]);

  // 5 секций × 47 карточек (Task 16) — единый запрос /api/metrics/dashboard
  useEffect(() => {
    api<DashboardMetricsResponse>(
      `/api/metrics/dashboard?${apiQueryString}`,
    )
      .then(setAllMetrics)
      .catch(() => setAllMetrics(null));
  }, [apiQueryString]);

  // Окно атрибуции (lookback) — как далеко до заказа ищем рекламный клик (7/14/30/90)
  const [lookback, setLookback] = useState(30);

  // Extended dashboard data — Pins, Web Analytics, Store, Attribution
  useEffect(() => {
    fetchDashboardExtended(days, lookback).then(setExtended).catch(() => setExtended(null));
  }, [days, lookback]);

  // Deep analytics — audience, heatmap (30 дней, при mount)
  useEffect(() => {
    fetchHourPatterns(30).then(setHours).catch(() => setHours(null));
  }, []);

  // КИТ P&L по выбранному периоду — для прибыли/маржинальности шапки (от выручки-выкупа)
  useEffect(() => {
    fetchPnL({ from: periodState.from, to: periodState.to })
      .then(setPnl)
      .catch(() => setPnl(null));
  }, [periodState.from, periodState.to]);

  async function handleRunSync() {
    setSyncing(true);
    setSyncResult(null);
    try {
      const r = await api<{ ok: boolean; ok_count: number; fail_count: number; results: Record<string, { ok: boolean; error?: string }> }>("/api/sync/run-all", {
        method: "POST",
        body: JSON.stringify({ days: 14 }),
      });
      const summary = `${r.ok_count}/${r.ok_count + r.fail_count} ok`;
      const failed = Object.entries(r.results).filter(([, v]) => !v.ok).map(([k, v]) => `${k}: ${v.error}`).join("; ");
      setSyncResult(failed ? `${summary} · failures: ${failed}` : summary);
      setTimeout(() => window.location.reload(), 1500);
    } catch (e) {
      setSyncResult(`error: ${(e as Error).message}`);
    } finally {
      setSyncing(false);
    }
  }

  if (loading && !data) {
    return <div style={{ color: "var(--text-secondary)" }}>Загрузка…</div>;
  }
  if (error) {
    return <div style={{ color: "var(--status-bad-fg)" }}>Ошибка: {error}</div>;
  }
  if (!data) return null;

  // Helper: достать данные конкретной метрики из allMetrics, перебирая 5 секций.
  const getMetric = (
    id: MetricId,
  ): { current: number | null; previous: number | null; sparkline: ChartPoint[] } => {
    if (!allMetrics) return { current: null, previous: null, sparkline: [] };
    const sections = [allMetrics.money, allMetrics.reach, allMetrics.cpa, allMetrics.cr, allMetrics.quality];
    for (const sec of sections) {
      if (id in sec.current) {
        return {
          current: sec.current[id] ?? null,
          previous: sec.previous[id] ?? null,
          sparkline: adaptSparkline(id, sec.sparklines[id]),
        };
      }
    }
    return { current: null, previous: null, sparkline: [] };
  };

  // ── KPI STRIP (8) — funnel order: вход → конверсии → выход ─────────────
  const KPI_IDS: MetricId[] = [
    "cost_rub",        // расход
    "visits",          // посетители (NEW)
    "reach_basket",    // корзины
    "reach_order_placed", // заказы
    "gross_revenue",   // выручка
    "roas",            // ROAS
    "cr_order_to_buyout", // % выкупа
    "cp_order",        // CPA
  ];
  const kpiItems: KPIStripItem[] = KPI_IDS.map((id) => {
    const d = getMetric(id);
    return { id, current: d.current, previous: d.previous, sparkline: d.sparkline };
  });

  // ── FUNNEL HERO (7 ступеней) ───────────────────────────────────────────
  const FUNNEL_STEPS: Array<{ id: MetricId; cpaId?: MetricId }> = [
    { id: "reach_product_list_view", cpaId: "cpm" },
    { id: "reach_product_view", cpaId: "cp_product_view" },
    { id: "reach_product_click", cpaId: "cp_product_click" },
    { id: "reach_basket", cpaId: "cp_basket" },
    { id: "reach_checkout", cpaId: "cp_checkout" },
    { id: "reach_order_placed", cpaId: "cp_order" },
    { id: "reach_order_delivered", cpaId: "cp_buyout" },
  ];
  const funnelHeroSteps: FunnelHeroStep[] = FUNNEL_STEPS.map(({ id, cpaId }) => ({
    id,
    reach: getMetric(id).current,
    cpaId,
    cpa: cpaId ? getMetric(cpaId).current : null,
  }));

  // ── CR RIBBON (5 ячеек) ────────────────────────────────────────────────
  const CR_IDS: MetricId[] = [
    "cr_click_to_visit",
    "cr_visit_to_basket",
    "cr_basket_to_checkout",
    "cr_checkout_to_order",
    "cr_order_to_buyout",
  ];
  const crSteps = CR_IDS.map((id) => {
    const d = getMetric(id);
    return { id, current: d.current, previous: d.previous };
  });

  // ── ПРОДАЖИ — ИТОГ (шапка: дашборд про ВСЕ продажи; реклама — её часть) ──
  const mc = allMetrics?.money.current;
  const mp = allMetrics?.money.previous;
  const deltaPct = (cur?: number | null, prev?: number | null): number | null =>
    cur != null && prev != null && prev !== 0 ? (cur - prev) / prev : null;
  const salesRevenue = mc?.gross_revenue ?? null;
  const salesBuyout = mc?.net_revenue ?? null;
  const salesOrders = mc?.orders ?? null;
  const salesAov = mc?.aov ?? null;          // чек заказа
  const salesTrueAov = mc?.true_aov ?? null; // чек выкупа
  const adCost = mc?.cost_rub ?? null;
  const mer = salesBuyout != null && adCost != null && adCost > 0 ? salesBuyout / adCost : null;
  const drr = salesBuyout != null && adCost != null && salesBuyout > 0 ? adCost / salesBuyout : null;

  return (
    <div>
      <PageHeader
        title="Дашборд"
        description="Six Reasons · сравнение vs прошлый период"
        rightSlot={
          <>
            <button
              onClick={handleRunSync}
              disabled={syncing}
              className="primary"
              style={{ minWidth: 160 }}
            >
              {syncing ? "Обновляю…" : "Обновить данные"}
            </button>
            {syncResult && (
              <span style={{ fontSize: 11, color: "var(--text-secondary)", maxWidth: 400 }}>
                {syncResult}
              </span>
            )}
          </>
        }
      />

      {/* PERIOD BAR — chips + comparison */}
      <div
        style={{
          display: "flex",
          gap: 12,
          alignItems: "center",
          margin: "16px 0 24px",
          flexWrap: "wrap",
        }}
      >
        <DateRangeChips
          preset={periodState.preset}
          from={periodState.from}
          to={periodState.to}
          onPreset={setPreset}
          onRange={setRange}
        />
        <ComparisonSelector vs={periodState.vs} onChange={setVs} />
      </div>

      {/* ПРОДАЖИ — ИТОГ (главное: дашборд про все продажи, реклама — её часть) */}
      <SalesHeadline
        revenue={salesRevenue} revenueDelta={deltaPct(mc?.gross_revenue, mp?.gross_revenue)}
        buyout={salesBuyout} buyoutDelta={deltaPct(mc?.net_revenue, mp?.net_revenue)}
        orders={salesOrders} ordersDelta={deltaPct(mc?.orders, mp?.orders)}
        aov={salesAov} trueAov={salesTrueAov}
        profit={pnl?.profit_total ?? null}
        marginPct={pnl && pnl.buyout_revenue > 0 ? pnl.profit_total / pnl.buyout_revenue : null}
        mer={mer} adCost={adCost} drr={drr}
        ncBuyout={extended?.customerMix?.new_buyout_revenue ?? null}
        newCustomers={extended?.customerMix?.new_customers ?? null}
        returningCustomers={extended?.customerMix?.returning_customers ?? null}
        comparisonLabel={comparisonLabel(periodState.vs)}
      />

      {/* ОТКУДА ПРОДАЖИ — реклама как часть воронки (платный vs органика) */}
      {extended && <SalesSourceBlock s={extended.bySource} j={extended.buyerJourney} />}

      {/* МОДЕЛИ АТРИБУЦИИ — как считать рекламный выкуп (первое/последнее/распыление) */}
      {extended && <DashboardAttrModels m={extended.attributionModels} />}

      {/* ИСТОЧНИК ТРАФИКА — свежая реклама / послерекламный шлейф / органика */}
      {extended && <TrafficSourcesBlock t={extended.trafficSources} bots={extended.botStats} />}

      {/* СВЕРКА ДИРЕКТ↔МЕТРИКА — показы → клики (оплачено) → переходы */}
      {extended && <AdReconciliationBlock r={extended.adReconciliation} />}

      {/* ОКНО АТРИБУЦИИ — общий lookback для лага и пути покупателя */}
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12, fontSize: 12, color: "var(--text-secondary)" }}>
        <span style={{ textTransform: "uppercase", letterSpacing: "0.04em", fontSize: 10, color: "var(--text-muted)" }}>Окно атрибуции</span>
        <div style={{ display: "inline-flex", border: "1px solid var(--border-default)", borderRadius: 8, overflow: "hidden" }}>
          {[7, 14, 30, 90].map((d) => (
            <button key={d} onClick={() => setLookback(d)} style={{
              border: "none", padding: "5px 12px", fontSize: 12, cursor: "pointer",
              background: lookback === d ? "var(--brand-primary)" : "var(--surface-card)",
              color: lookback === d ? "#fff" : "var(--text-secondary)", fontWeight: lookback === d ? 600 : 500,
            }}>{d}д</button>
          ))}
        </div>
        <span style={{ color: "var(--text-muted)" }}>— как далеко до заказа ищем рекламный клик (влияет на «лаг» и «путь покупателя»)</span>
      </div>

      {/* ЛАГ РЕКЛАМА→ВЫКУП — через сколько дней после клика созревает выкуп */}
      {extended && <AdPurchaseLagBlock lag={extended.adPurchaseLag} />}

      {/* ПУТЬ ПОКУПАТЕЛЯ — реклама-сразу / реклама-ассист / без рекламы */}
      {extended && <BuyerJourneyBlock j={extended.buyerJourney} />}

      {/* 8 KPI STRIP — компактная сводка по воронке/эффективности (информативная) */}
      <KPIStrip
        metrics={kpiItems}
        activeMetricId={null}
        onActivate={() => {}}
      />

      {/* COMBO TREND — затраты + заказы ₽ + посетители + заказы шт + net-ROAS */}
      <ComboTrendChart
        costSeries={(allMetrics?.money.sparklines.cost_rub ?? []).map((p) => ({ day: p.day, value: p.value }))}
        revenueSeries={(allMetrics?.money.sparklines.gross_revenue ?? []).map((p) => ({ day: p.day, value: p.value }))}
        visitsSeries={(allMetrics?.money.sparklines.visits ?? []).map((p) => ({ day: p.day, value: p.value }))}
        ordersSeries={(allMetrics?.reach.sparklines.reach_order_placed ?? []).map((p) => ({ day: p.day, value: p.value }))}
        roasSeries={(allMetrics?.money.sparklines.net_roas ?? []).map((p) => ({ day: p.day, value: p.value }))}
      />

      {/* FUNNEL HERO */}
      <FunnelHero steps={funnelHeroSteps} />

      {/* CR RIBBON */}
      <div style={{ marginBottom: 8 }}>
        <h3
          style={{
            fontSize: 11,
            textTransform: "uppercase",
            letterSpacing: "0.06em",
            color: "var(--text-secondary)",
            fontWeight: 600,
            margin: "0 0 10px",
          }}
        >
          Конверсии между шагами
        </h3>
      </div>
      <CRRibbon steps={crSteps} />

      {/* === Качество визита (уникальные метрики, нет в KPI/воронке выше) === */}
      <MetricSection title="Качество визита · весь трафик" cols={4}>
        {(["bounce_rate", "visit_duration", "pages_per_visit", "pct_new_users"] as MetricId[]).map((id) => (
          <MetricCard
            key={id}
            metricId={id}
            current={allMetrics?.quality.current[id] ?? null}
            previous={allMetrics?.quality.previous[id] ?? null}
            comparisonLabel={comparisonLabel(periodState.vs)}
            sparkline={adaptSparkline(id, allMetrics?.quality.sparklines[id])}
          />
        ))}
      </MetricSection>

      {/* Полный P&L — на отдельном экране «Юнит-экономика» (источник КИТ) */}
      <div style={{ marginBottom: 24, fontSize: 13 }}>
        <Link to="/pnl" style={{ color: "var(--brand-primary)", textDecoration: "none", fontWeight: 600 }}>
          Полный P&L (выкуп − себестоимость − доставка − эквайринг − налог − реклама) → Юнит-экономика
        </Link>
      </div>

      {/* Audience — единый блок: срез × таблица-рейтинг (реальные метрики сегмента) */}
      <AudiencePanel days={30} defaultDimension="gender_age" />

      {/* Hour heatmap */}
      {hours && hours.length > 0 && (
        <div style={{ marginBottom: 32 }}>
          <SectionTitle title="Когда покупают" icon={Clock} description="30 дней · день недели × час по выручке" />
          <Card padding={16}>
            <HourHeatmap
              cells={hours.map((h) => ({ hour: h.hour, dayOfWeek: h.day_of_week, value: h.revenue }))}
            />
          </Card>
        </div>
      )}

    </div>
  );
}



// ── ПРОДАЖИ — ИТОГ ─────────────────────────────────────────────────────────
// Шапка дашборда: главное — продажи (выручка/выкуп КИТ, заказы, средний чек,
// прибыль). Реклама показана тонкой строкой как ЧАСТЬ продаж (MER/расход/ДРР),
// а не как итог экрана.
function SalesHeadline(props: {
  revenue: number | null; revenueDelta: number | null;   // Заказы, ₽ (gross, сумма заказов)
  buyout: number | null; buyoutDelta: number | null;     // Выручка (выкуп) = нетто-выкуп
  orders: number | null; ordersDelta: number | null;     // Заказы, шт
  aov: number | null;                                    // Чек заказа
  trueAov: number | null;                                // Чек выкупа
  profit: number | null; marginPct: number | null;
  mer: number | null; adCost: number | null; drr: number | null;
  ncBuyout: number | null;                               // выручка новых клиентов (выкуп)
  newCustomers: number | null; returningCustomers: number | null;
  comparisonLabel: string;
}) {
  const ncMer = props.ncBuyout != null && props.adCost != null && props.adCost > 0 ? props.ncBuyout / props.adCost : null;
  const cell = (label: string, value: string, delta: number | null, accent?: boolean) => {
    const dColor = delta == null ? "var(--text-muted)" : delta >= 0 ? "var(--status-good-fg)" : "var(--status-bad-fg)";
    return (
      <div style={{ flex: 1, minWidth: 120, padding: "2px 4px" }}>
        <div style={{ fontSize: 11, color: "var(--text-secondary)", marginBottom: 4 }}>{label}</div>
        <div style={{ fontSize: 26, fontWeight: 700, color: accent ? "var(--status-good-fg)" : "var(--text-primary)", fontVariantNumeric: "tabular-nums", lineHeight: 1.1 }}>
          {value}
        </div>
        {delta != null && (
          <div style={{ fontSize: 11, color: dColor, marginTop: 3, fontVariantNumeric: "tabular-nums" }}>
            {delta >= 0 ? "▲" : "▼"} {formatPct(Math.abs(delta), 0)} <span style={{ color: "var(--text-muted)" }}>{props.comparisonLabel}</span>
          </div>
        )}
      </div>
    );
  };
  const profitColor = props.profit == null ? "var(--text-primary)" : props.profit >= 0 ? "var(--status-good-fg)" : "var(--status-bad-fg)";
  return (
    <div style={{
      background: "var(--surface-card)", border: "1px solid var(--border-default)",
      borderRadius: 12, padding: "16px 18px", marginBottom: 16,
    }}>
      <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--text-secondary)", fontWeight: 600, marginBottom: 12 }}>
        Продажи — итог · вся воронка
      </div>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "flex-end" }}>
        {cell("Выручка (выкуп)", props.buyout != null ? formatRub(props.buyout) : "—", props.buyoutDelta, true)}
        {cell("Заказы, ₽", props.revenue != null ? formatRub(props.revenue) : "—", props.revenueDelta)}
        {cell("Заказы, шт", props.orders != null ? formatInt(props.orders) : "—", props.ordersDelta)}
        <div style={{ flex: 1, minWidth: 120, padding: "2px 4px" }}>
          <div style={{ fontSize: 11, color: "var(--text-secondary)", marginBottom: 4 }}>Средний чек</div>
          <div style={{ fontSize: 26, fontWeight: 700, color: "var(--text-primary)", fontVariantNumeric: "tabular-nums", lineHeight: 1.1 }}>
            {props.trueAov != null ? formatRub(props.trueAov) : "—"}
          </div>
          <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 3 }}>
            выкуп · заказ {props.aov != null ? formatRub(props.aov) : "—"}
          </div>
        </div>
        <div style={{ flex: 1, minWidth: 120, padding: "2px 4px" }}>
          <div style={{ fontSize: 11, color: "var(--text-secondary)", marginBottom: 4 }}>Прибыль</div>
          <div style={{ fontSize: 26, fontWeight: 700, color: profitColor, fontVariantNumeric: "tabular-nums", lineHeight: 1.1 }}>
            {props.profit != null ? formatRub(props.profit) : "—"}
          </div>
          {props.marginPct != null && (
            <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 3 }}>маржинальность {(props.marginPct * 100).toFixed(1).replace(".", ",")}%</div>
          )}
        </div>
      </div>
      {/* Реклама — ЧАСТЬ продаж */}
      <div style={{ marginTop: 12, paddingTop: 10, borderTop: "1px solid var(--border-default)", fontSize: 12, color: "var(--text-secondary)" }}>
        <span style={{ color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.04em", fontSize: 10, marginRight: 8 }}>Реклама — часть продаж</span>
        MER (выручка ÷ весь расход): <strong style={{ color: props.mer != null && props.mer >= 1 ? "var(--status-good-fg)" : "var(--text-primary)" }}>{props.mer != null ? props.mer.toLocaleString("ru-RU", { maximumFractionDigits: 2 }) : "—"}</strong>
        {ncMer != null && <> {" · "}<span title="New-Customer MER: выручка НОВЫХ клиентов ÷ весь расход. Главный сигнал качества привлечения (лидеры считают его важнее общего MER).">NC-MER (новые ÷ расход): <strong style={{ color: ncMer >= 1 ? "var(--status-good-fg)" : "var(--status-bad-fg)" }}>{ncMer.toLocaleString("ru-RU", { maximumFractionDigits: 2 })}</strong></span></>}
        {" · "}расход: <strong style={{ color: "var(--text-primary)" }}>{props.adCost != null ? formatRub(props.adCost) : "—"}</strong>
        {props.drr != null && <> {" · "}ДРР (расход ÷ выручку): <strong style={{ color: "var(--text-primary)" }}>{formatPct(props.drr, 0)}</strong></>}
      </div>
      {/* Клиенты — реальные люди по заказам КИТ (email/phone), как Shopify */}
      {(props.newCustomers != null || props.returningCustomers != null) && (
        <div style={{ marginTop: 8, fontSize: 12, color: "var(--text-secondary)" }}>
          <span style={{ color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.04em", fontSize: 10, marginRight: 8 }}>Клиенты (по заказам)</span>
          Новых: <strong style={{ color: "var(--status-good-fg)" }}>{props.newCustomers != null ? formatInt(props.newCustomers) : "—"}</strong>
          {" · "}повторных: <strong style={{ color: "var(--text-primary)" }}>{props.returningCustomers != null ? formatInt(props.returningCustomers) : "—"}</strong>
          {props.ncBuyout != null && <> {" · "}выручка новых: <strong style={{ color: "var(--text-primary)" }}>{formatRub(props.ncBuyout)}</strong></>}
          <span style={{ color: "var(--text-muted)" }}> — реальные люди по email/телефону, не cookie-визиты</span>
        </div>
      )}
    </div>
  );
}

// ── ОТКУДА ПРОДАЖИ ─────────────────────────────────────────────────────────
// Реклама — ЧАСТЬ воронки. Показываем долю платного трафика (Я.Директ) vs
// органики/прямого в выкупе-выручке + таблицу визиты/выкупы/выручка.
function SalesSourceBlock({ s, j }: { s: SalesBySource; j: BuyerJourney }) {
  // Выкуп — из «Пути покупателя» (ассист-aware + карта заказ→клиент): реклама =
  // прямая + ассист, органика = organic + не видела Метрика. Визиты — из bySource.
  const g = (k: string) => j.groups.find((x) => x.key === k) ?? { orders: 0, revenue: 0 };
  const adOrders = g("ad_direct").orders + g("ad_assisted").orders;
  const adRev = g("ad_direct").revenue + g("ad_assisted").revenue;
  const orgOrders = g("organic").orders + g("untracked").orders;
  const orgRev = g("organic").revenue + g("untracked").revenue;
  const totalRev = adRev + orgRev;
  const paidPct = totalRev > 0 ? (adRev / totalRev) * 100 : 0;
  const orgPct = Math.max(0, 100 - paidPct);
  const COL_PAID = "#0EA5E9";
  const COL_ORG = "#10B981";
  const th: CSSProperties = { padding: "6px 10px", fontSize: 11, textTransform: "uppercase", letterSpacing: "0.04em", color: "var(--text-secondary)", fontWeight: 600, borderBottom: "1px solid var(--border-default)" };
  const thR: CSSProperties = { ...th, textAlign: "right" };
  const td: CSSProperties = { padding: "6px 10px", borderBottom: "1px solid var(--border-default)", color: "var(--text-secondary)" };
  const tdR: CSSProperties = { ...td, textAlign: "right", fontVariantNumeric: "tabular-nums", color: "var(--text-primary)" };
  const dot = (c: string) => <span style={{ display: "inline-block", width: 8, height: 8, borderRadius: 2, background: c, marginRight: 6 }} />;
  return (
    <div style={{ background: "var(--surface-card)", border: "1px solid var(--border-default)", borderRadius: 12, padding: "16px 18px", marginBottom: 16 }}>
      <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--text-secondary)", fontWeight: 600, marginBottom: 10 }}>
        Откуда продажи · реклама — часть воронки
      </div>
      {/* Доля в выкупе-выручке */}
      <div style={{ display: "flex", height: 26, borderRadius: 6, overflow: "hidden", marginBottom: 8 }}>
        <div style={{ width: `${paidPct}%`, background: COL_PAID, minWidth: paidPct > 0 ? 2 : 0 }} />
        <div style={{ width: `${orgPct}%`, background: COL_ORG, minWidth: orgPct > 0 ? 2 : 0 }} />
      </div>
      <div style={{ fontSize: 12, color: "var(--text-secondary)", marginBottom: 14 }}>
        {dot(COL_PAID)}Реклама (прямая + ассист): <strong style={{ color: "var(--text-primary)" }}>{paidPct.toFixed(0)}%</strong> выручки
        <span style={{ margin: "0 10px", color: "var(--text-muted)" }}>·</span>
        {dot(COL_ORG)}Органика / прямой: <strong style={{ color: "var(--text-primary)" }}>{orgPct.toFixed(0)}%</strong>
      </div>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
        <thead>
          <tr>
            <th style={th}>Источник</th>
            <th style={thR}>Визиты</th>
            <th style={thR}>Выкупов</th>
            <th style={thR}>Выручка ₽</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td style={td}>{dot(COL_PAID)}Реклама (прямая + ассист)</td>
            <td style={tdR}>{formatInt(s.paid.visits)}</td>
            <td style={tdR}>{formatInt(adOrders)}</td>
            <td style={tdR}>{formatRub(adRev)}</td>
          </tr>
          <tr>
            <td style={td}>{dot(COL_ORG)}Органика / прямой</td>
            <td style={tdR}>{formatInt(s.organic_other.visits)}</td>
            <td style={tdR}>{formatInt(orgOrders)}</td>
            <td style={tdR}>{formatRub(orgRev)}</td>
          </tr>
          <tr>
            <td style={{ ...td, color: "var(--text-primary)", fontWeight: 600, borderBottom: "none" }}>Всего</td>
            <td style={{ ...tdR, fontWeight: 600, borderBottom: "none" }}>{formatInt(s.total.visits)}</td>
            <td style={{ ...tdR, fontWeight: 600, borderBottom: "none" }}>{formatInt(adOrders + orgOrders)}</td>
            <td style={{ ...tdR, fontWeight: 600, borderBottom: "none" }}>{formatRub(totalRev)}</td>
          </tr>
        </tbody>
      </table>
      <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 8, lineHeight: 1.5 }}>
        Выкуп — по «Пути покупателя»: реклама = прямая + ассист (вся заслуга рекламы, не только last-click), органика = без рекламного касания. Восстановлено через карту заказ→клиент Метрики. Визиты = посетители (платные — с меткой Я.Директа). Сегодня исключён.
      </div>
    </div>
  );
}

// ── МОДЕЛИ АТРИБУЦИИ (компактно) ───────────────────────────────────────────
// Как считать «рекламный» выкуп: первое касание / последнее / полное распыление.
// Один и тот же расход — три модели. Расхождение = чувствительность к модели.
function DashboardAttrModels({ m }: { m: AttributionModels }) {
  const th: CSSProperties = { padding: "6px 10px", fontSize: 11, textTransform: "uppercase", letterSpacing: "0.04em", color: "var(--text-secondary)", fontWeight: 600, borderBottom: "1px solid var(--border-default)" };
  const thR: CSSProperties = { ...th, textAlign: "right" };
  const lbl: CSSProperties = { padding: "6px 10px", color: "var(--text-secondary)", borderBottom: "1px solid var(--border-default)" };
  const num: CSSProperties = { padding: "6px 10px", textAlign: "right", fontVariantNumeric: "tabular-nums", color: "var(--text-primary)", borderBottom: "1px solid var(--border-default)" };
  const rows: { label: string; mod: AttributionModels["first_click"] }[] = [
    { label: "Первое касание", mod: m.first_click },
    { label: "Последнее касание", mod: m.last_click },
    { label: "Полное распыление", mod: m.linear },
  ];
  return (
    <div style={{ background: "var(--surface-card)", border: "1px solid var(--border-default)", borderRadius: 12, padding: "16px 18px", marginBottom: 16 }}>
      <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--text-secondary)", fontWeight: 600, marginBottom: 4 }}>
        Модели атрибуции · что считать рекламным выкупом
      </div>
      <div style={{ fontSize: 11, color: "var(--text-muted)", marginBottom: 10, lineHeight: 1.5 }}>
        Один и тот же расход под тремя моделями. Распыление = заказ делится поровну по всем касаниям (вкл. органику). Расхождение моделей = насколько оценка рекламы зависит от того, кому засчитать заказ.
      </div>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
        <thead>
          <tr>
            <th style={th}>Модель</th>
            <th style={thR}>[SR] ₽</th>
            <th style={thR}>Агентство ₽</th>
            <th style={thR}>Всего выкуп ₽</th>
            <th style={thR}>ROAS (всего)</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.label}>
              <td style={lbl}>{r.label}</td>
              <td style={num}>{formatRub(r.mod.sr.revenue)}</td>
              <td style={num}>{formatRub(r.mod.agency.revenue)}</td>
              <td style={{ ...num, fontWeight: 600 }}>{formatRub(r.mod.total.revenue)}</td>
              <td style={{ ...num, color: r.mod.total.roas != null && r.mod.total.roas >= 1 ? "var(--status-good-fg)" : "var(--text-muted)" }}>
                {r.mod.total.roas != null ? r.mod.total.roas.toLocaleString("ru-RU", { maximumFractionDigits: 2 }) : "—"}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 8 }}>
        Расход (весь Я.Директ): {formatRub(m.spend_sr + m.spend_agency)} · [SR] {formatRub(m.spend_sr)} · агентство {formatRub(m.spend_agency)}. Это ROAS-взгляд (только привязанный выкуп); MER считает весь выкуп.
      </div>
    </div>
  );
}

// ── ИСТОЧНИК ТРАФИКА ───────────────────────────────────────────────────────
// Свежая реклама (живой клик) / послерекламный шлейф (был прошлый клик) / органика.
// Стэк по дням — видно, как после отключения рекламы тает шлейф и проступает органика.
const C_FRESH = "#0EA5E9";
const C_LAGGED = "#F59E0B";
const C_ORGANIC = "#10B981";
const C_SPEND = "#EF4444";
function TrafficSourcesBlock({ t, bots }: { t: TrafficSources; bots?: BotStats }) {
  const tot = t.total;
  const sum = tot.fresh_ad + tot.lagged_ad + tot.organic;
  const pct = (n: number) => (sum > 0 ? Math.round((n / sum) * 100) : 0);
  const dot = (c: string) => <span style={{ display: "inline-block", width: 8, height: 8, borderRadius: 2, background: c, marginRight: 6 }} />;
  const fmtD = (s: string) => (s ? s.slice(8, 10) + "." + s.slice(5, 7) : "");
  return (
    <div style={{ background: "var(--surface-card)", border: "1px solid var(--border-default)", borderRadius: 12, padding: "16px 18px", marginBottom: 16 }}>
      <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--text-secondary)", fontWeight: 600, marginBottom: 4 }}>
        Источник трафика · свежая реклама / шлейф / органика
      </div>
      <div style={{ fontSize: 11, color: "var(--text-muted)", marginBottom: 10, lineHeight: 1.5 }}>
        <strong style={{ color: "var(--text-secondary)" }}>Свежая реклама</strong> — метка рекламы И был расход в этот день. <strong style={{ color: "var(--text-secondary)" }}>Шлейф</strong> — либо пришёл сам, но реклама его приводила за 90 дней, либо метка рекламы есть, а расхода в этот день нет (клик по старой ссылке). <strong style={{ color: "var(--text-secondary)" }}>Органика</strong> — рекламных кликов не было. Красная линия — расход Я.Директа (синий столбец есть только там, где линия выше нуля).
        {t.max_spend_day && <> Расход синкается с лагом ~1-2 дня: последний день с расходом — {fmtD(t.max_spend_day)} (правее реклама временно уедет в шлейф, пока расход не догрузится).</>}
        {bots && bots.filtered_clients > 0 && (
          <> {" "}🤖 Боты исключены: {formatInt(bots.filtered_clients)} клиентов ({formatInt(bots.filtered_visits)} визитов, пик ≥{bots.peak_threshold}/день, 0 покупок). Покупателей среди превысивших порог: {formatInt(bots.exempt_buyers)} — <strong>защищены</strong> (не фильтруются).</>
        )}
      </div>
      <div style={{ display: "flex", gap: 16, marginBottom: 12, flexWrap: "wrap", fontSize: 13 }}>
        <span>{dot(C_FRESH)}Свежая реклама: <strong style={{ color: "var(--text-primary)" }}>{formatInt(tot.fresh_ad)}</strong> ({pct(tot.fresh_ad)}%)</span>
        <span>{dot(C_LAGGED)}Шлейф: <strong style={{ color: "var(--text-primary)" }}>{formatInt(tot.lagged_ad)}</strong> ({pct(tot.lagged_ad)}%)</span>
        <span>{dot(C_ORGANIC)}Органика: <strong style={{ color: "var(--text-primary)" }}>{formatInt(tot.organic)}</strong> ({pct(tot.organic)}%)</span>
        <span>{dot(C_SPEND)}Расход — линия (правая ось)</span>
      </div>
      <ResponsiveContainer width="100%" height={240}>
        <ComposedChart data={t.days} margin={{ top: 4, right: 12, bottom: 4, left: 8 }}>
          <CartesianGrid stroke={chartTheme.gridStroke} vertical={false} />
          <XAxis dataKey="day" tickFormatter={fmtD} tick={{ fontSize: 10, fill: chartTheme.axisTickFill }} minTickGap={20} />
          <YAxis yAxisId="left" tick={{ fontSize: 10, fill: chartTheme.axisTickFill }} width={40} />
          <YAxis yAxisId="right" orientation="right" tickFormatter={(v) => `${Math.round(Number(v) / 1000)}к`} tick={{ fontSize: 10, fill: chartTheme.axisTickFill }} width={42} />
          <Tooltip contentStyle={{ background: chartTheme.tooltipBg, border: chartTheme.tooltipBorder, borderRadius: 6, fontSize: 12 }} />
          <Legend wrapperStyle={{ fontSize: 11 }} />
          <Bar yAxisId="left" dataKey="fresh_ad" stackId="t" name="Свежая реклама" fill={C_FRESH} />
          <Bar yAxisId="left" dataKey="lagged_ad" stackId="t" name="Шлейф" fill={C_LAGGED} />
          <Bar yAxisId="left" dataKey="organic" stackId="t" name="Органика" fill={C_ORGANIC} radius={[2, 2, 0, 0]} />
          <Line yAxisId="right" type="monotone" dataKey="spend" name="Расход ₽" stroke={C_SPEND} strokeWidth={2} dot={false} connectNulls={false} isAnimationActive={false} />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}

// ── СВЕРКА ДИРЕКТ ↔ МЕТРИКА: показы → клики → переходы ──────────────────────
// Клики Директа = реально оплаченная реклама. ad-переходы Метрики обычно в 2-3 раза
// больше (возвраты по метке / шлейф). Линия кликов поверх столбцов переходов = сколько
// «рекламного» трафика реально оплачено.
function AdReconciliationBlock({ r }: { r: AdReconciliation }) {
  const ratio = r.total_clicks > 0 ? r.total_ad_visits / r.total_clicks : null;
  const paidPct = r.total_ad_visits > 0 ? Math.round((r.total_clicks / r.total_ad_visits) * 100) : null;
  const fmtD = (s: string) => (s ? s.slice(8, 10) + "." + s.slice(5, 7) : "");
  const C_VISITS = "#0EA5E9", C_CLICKS = "#EF4444", C_IMPR = "#CBD5E1";
  const dot = (c: string) => <span style={{ display: "inline-block", width: 8, height: 8, borderRadius: 2, background: c, marginRight: 6 }} />;
  return (
    <div style={{ background: "var(--surface-card)", border: "1px solid var(--border-default)", borderRadius: 12, padding: "16px 18px", marginBottom: 16 }}>
      <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--text-secondary)", fontWeight: 600, marginBottom: 4 }}>
        Реклама: показы → клики → переходы · сверка Директ ↔ Метрика
      </div>
      <div style={{ fontSize: 11, color: "var(--text-muted)", marginBottom: 10, lineHeight: 1.5 }}>
        <strong style={{ color: C_CLICKS }}>Клики Директа</strong> — реально оплаченная реклама. <strong style={{ color: C_VISITS }}>Переходы Метрики</strong> с меткой «ad» обычно в 2-3 раза больше: разница (столбец выше красной линии) = возвраты по той же метке / шлейф, за которые мы не платим. Где линия у нуля, а столбец есть — весь рекламный трафик в этот день неоплачен (старые ссылки / лаг синка).
      </div>
      <div style={{ display: "flex", gap: 16, marginBottom: 12, flexWrap: "wrap", fontSize: 13 }}>
        <span>{dot(C_IMPR)}Показы: <strong style={{ color: "var(--text-primary)" }}>{formatInt(r.total_impressions)}</strong></span>
        <span>{dot(C_CLICKS)}Клики (оплачено): <strong style={{ color: "var(--text-primary)" }}>{formatInt(r.total_clicks)}</strong></span>
        <span>{dot(C_VISITS)}ad-переходы: <strong style={{ color: "var(--text-primary)" }}>{formatInt(r.total_ad_visits)}</strong></span>
        {ratio != null && <span>Переходов на клик: <strong style={{ color: "var(--text-primary)" }}>{ratio.toLocaleString("ru-RU", { maximumFractionDigits: 1 })}</strong></span>}
        {paidPct != null && <span>Оплачено переходов: <strong style={{ color: paidPct >= 60 ? "var(--status-good-fg)" : "var(--status-bad-fg)" }}>{paidPct}%</strong></span>}
      </div>
      <ResponsiveContainer width="100%" height={230}>
        <ComposedChart data={r.days} margin={{ top: 4, right: 12, bottom: 4, left: 8 }}>
          <CartesianGrid stroke={chartTheme.gridStroke} vertical={false} />
          <XAxis dataKey="day" tickFormatter={fmtD} tick={{ fontSize: 10, fill: chartTheme.axisTickFill }} minTickGap={18} />
          <YAxis yAxisId="left" tick={{ fontSize: 10, fill: chartTheme.axisTickFill }} width={40} />
          <YAxis yAxisId="right" orientation="right" tickFormatter={(v) => `${Math.round(Number(v) / 1000)}к`} tick={{ fontSize: 10, fill: chartTheme.axisTickFill }} width={42} />
          <Tooltip contentStyle={{ background: chartTheme.tooltipBg, border: chartTheme.tooltipBorder, borderRadius: 6, fontSize: 12 }} />
          <Legend wrapperStyle={{ fontSize: 11 }} />
          <Bar yAxisId="left" dataKey="ad_visits" name="ad-переходы (Метрика)" fill={C_VISITS} radius={[2, 2, 0, 0]} />
          <Line yAxisId="left" type="monotone" dataKey="clicks" name="Клики (Директ, оплачено)" stroke={C_CLICKS} strokeWidth={2} dot={false} isAnimationActive={false} />
          <Line yAxisId="right" type="monotone" dataKey="impressions" name="Показы (Директ)" stroke={C_IMPR} strokeWidth={1.5} dot={false} isAnimationActive={false} />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}

// ── ЛАГ РЕКЛАМА → ВЫКУП ─────────────────────────────────────────────────────
// Через сколько дней после рекламного клика созревает выкуп. Отвечает на «с какого
// дня расхода пришли покупатели»: спенд сегодня даёт выкупы не только сегодня.
const LAG_COLORS: Record<string, string> = {
  d0: "#0EA5E9", d1_2: "#22C55E", d3_6: "#84CC16", d7_13: "#F59E0B", d14_30: "#F97316", no_ad: "#94A3B8",
};
function AdPurchaseLagBlock({ lag }: { lag: AdPurchaseLag }) {
  const tot = lag.total_buyout_orders;
  const pct = (n: number) => (tot > 0 ? Math.round((n / tot) * 100) : 0);
  const adOrders = lag.buckets.filter((b) => b.key !== "no_ad").reduce((a, b) => a + b.orders, 0);
  const sameDay = lag.buckets.find((b) => b.key === "d0")?.orders ?? 0;
  const delayed = adOrders - sameDay;
  const noAd = lag.buckets.find((b) => b.key === "no_ad")?.orders ?? 0;
  const lbl: CSSProperties = { padding: "6px 10px", color: "var(--text-secondary)", borderBottom: "1px solid var(--border-default)" };
  const num: CSSProperties = { padding: "6px 10px", textAlign: "right", fontVariantNumeric: "tabular-nums", color: "var(--text-primary)", borderBottom: "1px solid var(--border-default)" };
  const th: CSSProperties = { padding: "6px 10px", fontSize: 11, textTransform: "uppercase", letterSpacing: "0.04em", color: "var(--text-secondary)", fontWeight: 600, borderBottom: "1px solid var(--border-default)" };
  const thR: CSSProperties = { ...th, textAlign: "right" };
  const dot = (c: string) => <span style={{ display: "inline-block", width: 8, height: 8, borderRadius: 2, background: c, marginRight: 6 }} />;
  return (
    <div style={{ background: "var(--surface-card)", border: "1px solid var(--border-default)", borderRadius: 12, padding: "16px 18px", marginBottom: 16 }}>
      <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--text-secondary)", fontWeight: 600, marginBottom: 4 }}>
        Лаг реклама → выкуп · когда созревают покупки
      </div>
      <div style={{ fontSize: 11, color: "var(--text-muted)", marginBottom: 10, lineHeight: 1.5 }}>
        Через сколько дней после рекламного клика заказ становится выкупом. Спенд сегодня даёт выкупы не только сегодня — часть «дозревает» позже. «Без рекл. клика» = выкуп, у которого рекламного касания за 30 дней не было (органика/прочее).
      </div>
      {/* стэк-полоса по выкупам */}
      <div style={{ display: "flex", height: 26, borderRadius: 6, overflow: "hidden", marginBottom: 8 }}>
        {lag.buckets.filter((b) => b.orders > 0).map((b) => (
          <div key={b.key} title={`${b.label}: ${b.orders} (${pct(b.orders)}%)`}
            style={{ width: `${pct(b.orders)}%`, background: LAG_COLORS[b.key], minWidth: 2 }} />
        ))}
      </div>
      <div style={{ fontSize: 12, color: "var(--text-secondary)", marginBottom: 12 }}>
        В день клика: <strong style={{ color: "var(--text-primary)" }}>{pct(sameDay)}%</strong> · дозревает за 1–30 дней: <strong style={{ color: "var(--text-primary)" }}>{pct(delayed)}%</strong> · без рекл. клика: <strong style={{ color: "var(--text-primary)" }}>{pct(noAd)}%</strong> выкупов
      </div>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
        <thead>
          <tr><th style={th}>Лаг клик→выкуп</th><th style={thR}>Выкупов</th><th style={thR}>Доля</th><th style={thR}>Выручка ₽</th></tr>
        </thead>
        <tbody>
          {lag.buckets.map((b) => (
            <tr key={b.key}>
              <td style={lbl}>{dot(LAG_COLORS[b.key])}{b.label}</td>
              <td style={num}>{formatInt(b.orders)}</td>
              <td style={num}>{pct(b.orders)}%</td>
              <td style={num}>{formatRub(b.revenue)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── ПУТЬ ПОКУПАТЕЛЯ ─────────────────────────────────────────────────────────
// Классификация каждого выкупа по пути клиента: реклама-сразу / реклама-ассист
// (last-click это теряет) / без рекламы / Метрика не видела визит.
const JRN_COLORS: Record<string, string> = {
  ad_direct: "#0EA5E9", ad_assisted: "#8B5CF6", organic: "#10B981", untracked: "#94A3B8",
};
const JRN_METRICS: { key: "visits" | "carts" | "orders" | "buyouts"; label: string }[] = [
  { key: "visits", label: "Визиты" },
  { key: "carts", label: "Корзины" },
  { key: "orders", label: "Заказы" },
  { key: "buyouts", label: "Выкупы" },
];
function BuyerJourneyBlock({ j }: { j: BuyerJourney }) {
  const [metric, setMetric] = useState<"visits" | "carts" | "orders" | "buyouts">("buyouts");
  const tot = j.total_orders;
  const pct = (n: number) => (tot > 0 ? Math.round((n / tot) * 100) : 0);
  const lbl: CSSProperties = { padding: "6px 10px", color: "var(--text-secondary)", borderBottom: "1px solid var(--border-default)" };
  const num: CSSProperties = { padding: "6px 10px", textAlign: "right", fontVariantNumeric: "tabular-nums", color: "var(--text-primary)", borderBottom: "1px solid var(--border-default)" };
  const th: CSSProperties = { padding: "6px 10px", fontSize: 11, textTransform: "uppercase", letterSpacing: "0.04em", color: "var(--text-secondary)", fontWeight: 600, borderBottom: "1px solid var(--border-default)" };
  const thR: CSSProperties = { ...th, textAlign: "right" };
  const dot = (c: string) => <span style={{ display: "inline-block", width: 8, height: 8, borderRadius: 2, background: c, marginRight: 6 }} />;
  const assisted = j.groups.find((g) => g.key === "ad_assisted");
  return (
    <div style={{ background: "var(--surface-card)", border: "1px solid var(--border-default)", borderRadius: 12, padding: "16px 18px", marginBottom: 16 }}>
      <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--text-secondary)", fontWeight: 600, marginBottom: 4 }}>
        Путь покупателя · как клиент пришёл к выкупу
      </div>
      <div style={{ fontSize: 11, color: "var(--text-muted)", marginBottom: 10, lineHeight: 1.5 }}>
<strong style={{ color: JRN_COLORS.ad_direct }}>Реклама → сразу</strong> = рекламный клик в день, когда был расход (живая платная реклама). <strong style={{ color: "#8B5CF6" }}>Реклама-ассист</strong> — реклама причастна, но не свежий платный клик: либо реклама привела раньше и купил не с рекламы, либо клик по старой рекламной ссылке без расхода в этот день (шлейф). last-click этот вклад теряет (это модель «Last Non-Direct» у Northbeam). «Метрика не видела визит» — выкуп КИТ без сматченного визита. Переключатель — та же раскладка для визитов / корзин / заказов / выкупов (для визитов/корзин «не видела» нет). В дни без расхода (или с лагом синка ~1-2 дня) реклама-метка уедет в ассист, а не в «сразу».
      </div>
      {/* стэк-полоса по выкупам (итог) */}
      <div style={{ display: "flex", height: 22, borderRadius: 6, overflow: "hidden", marginBottom: 12 }}>
        {j.groups.filter((g) => g.orders > 0).map((g) => (
          <div key={g.key} title={`${g.label}: ${g.orders} (${pct(g.orders)}%)`}
            style={{ width: `${pct(g.orders)}%`, background: JRN_COLORS[g.key], minWidth: 2 }} />
        ))}
      </div>
      {/* переключатель метрики */}
      <div style={{ display: "inline-flex", border: "1px solid var(--border-default)", borderRadius: 8, overflow: "hidden", marginBottom: 10 }}>
        {JRN_METRICS.map((m) => (
          <button key={m.key} onClick={() => setMetric(m.key)} style={{
            border: "none", padding: "6px 14px", fontSize: 12, cursor: "pointer",
            background: metric === m.key ? "var(--brand-primary)" : "var(--surface-card)",
            color: metric === m.key ? "#fff" : "var(--text-secondary)", fontWeight: metric === m.key ? 600 : 500,
          }}>{m.label}</button>
        ))}
      </div>
      {/* по дням — стэк-бар выбранной метрики */}
      <ResponsiveContainer width="100%" height={200}>
        <BarChart data={j.timeline[metric]} margin={{ top: 4, right: 12, bottom: 4, left: 8 }}>
          <CartesianGrid stroke={chartTheme.gridStroke} vertical={false} />
          <XAxis dataKey="day" tickFormatter={(s) => (s ? s.slice(8, 10) + "." + s.slice(5, 7) : "")} tick={{ fontSize: 10, fill: chartTheme.axisTickFill }} minTickGap={18} />
          <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: chartTheme.axisTickFill }} width={36} />
          <Tooltip contentStyle={{ background: chartTheme.tooltipBg, border: chartTheme.tooltipBorder, borderRadius: 6, fontSize: 12 }} />
          <Legend wrapperStyle={{ fontSize: 11 }} />
          <Bar dataKey="ad_direct" stackId="j" name="Реклама → сразу" fill={JRN_COLORS.ad_direct} />
          <Bar dataKey="ad_assisted" stackId="j" name="Реклама-ассист" fill={JRN_COLORS.ad_assisted} />
          <Bar dataKey="organic" stackId="j" name="Без рекламы" fill={JRN_COLORS.organic} />
          <Bar dataKey="untracked" stackId="j" name="Не видела Метрика" fill={JRN_COLORS.untracked} radius={[2, 2, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13, marginTop: 12 }}>
        <thead>
          <tr><th style={th}>Путь</th><th style={thR}>Выкупов</th><th style={thR}>Клиентов</th><th style={thR}>Доля</th><th style={thR}>Выручка ₽</th></tr>
        </thead>
        <tbody>
          {j.groups.map((g) => (
            <tr key={g.key}>
              <td style={lbl}>{dot(JRN_COLORS[g.key])}{g.label}</td>
              <td style={num}>{formatInt(g.orders)}</td>
              <td style={num}>{g.key === "untracked" ? "—" : formatInt(g.customers)}</td>
              <td style={num}>{pct(g.orders)}%</td>
              <td style={num}>{formatRub(g.revenue)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {assisted && assisted.orders > 0 && (
        <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 8 }}>
          💡 Реклама-ассист = {formatInt(assisted.orders)} выкупов на {formatRub(assisted.revenue)} — это заслуга рекламы, которую last-click и ROAS не видят.
        </div>
      )}
    </div>
  );
}
